/// <amd-module name="Reporting/BoardsReportView/BoardsReportSelector" />
import PingServiceQuery from "Analytics/Queries/PingServiceQuery";
import * as React from "react";
import { AccessDeniedMessage } from "Reporting/BoardsCommon/AccessDeniedMessage";
import { getBoardsReportUrl } from "Reporting/BoardsCommon/boardsRoutingHelper";
import { resetQueryCache } from "Reporting/BoardsCommon/ReportQueryHelper";
import { publishEmptyReportTelemetry, telemetryAreaName } from "Reporting/BoardsCommon/ReportTelemetry";
import * as Resources from "Reporting/BoardsReportView/Resources";
import { NoOpComponent } from "VSS/Platform/Components/Layout";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { traceException } from "VSS/Platform/Trace";
import { Header, TitleSize } from "VSSUI/Header";
import { BoardsReportSelectorProps } from "./BoardsReportSelectorProps";
import { CfdReportCard } from "./CfdReportCard";
import { VelocityReportCard } from "./VelocityReportCard";

interface BoardsReportSelectorState {
    showAccessDeniedState?: boolean;
    isReady?: boolean;
}

/**
 * Responsible for handling report selection between CFD and Velocity.
 */
export class BoardsReportSelector extends VssComponent<BoardsReportSelectorProps, BoardsReportSelectorState> {
    public static readonly ReportTelemetryFeatureName = "BoardsReportSelector";
    constructor(props: BoardsReportSelectorProps, context?: IVssComponentContext | undefined) {
        super(props, context);
        this.state = {};
    }

    public async componentDidMount() {
        resetQueryCache(this.context.pageContext);
        this.intitializeBoardsReportSelectorState();
    }

    public async componentDidUpdate(prevProps: BoardsReportSelectorProps) {
        if (JSON.stringify(this.props) != JSON.stringify(prevProps)) {
            if (this.props.lastRefresh != prevProps.lastRefresh) {
                resetQueryCache(this.context.pageContext);
            }
            this.intitializeBoardsReportSelectorState();
        }
    }

    public render(): JSX.Element | null {
        let hideCardsClass = "";
        let accessDeniedMessage;
        if (!this.state.isReady) {
            hideCardsClass = " hidden";
        }
        if (this.state.showAccessDeniedState) {
            accessDeniedMessage = <AccessDeniedMessage />;
            hideCardsClass = " hidden";
        }
        return (
            <>
                {accessDeniedMessage}
                <div className={`backlogs-report-selector bolt-page-grey ${hideCardsClass}`}>
                    <Header titleSize={TitleSize.Large} title={Resources.ReportTitle} />
                    <div className="flex-row report-cards-view">
                        <CfdReportCard {...this.props} onCardClick={this.onClickCfd} />
                        <VelocityReportCard {...this.props} onCardClick={this.onClickVelocity} />
                    </div>
                </div>
            </>
        );
    }

    public onClickVelocity = (): void => {
        this.navigateLegacy("velocity");
    };

    public onClickCfd = (): void => {
        this.navigateLegacy("cfd");
    };

    public navigateLegacy = (report: string): void => {
        const destination = getBoardsReportUrl(this.context.pageContext, this.props.categoryName, report);
        const recordHistoryEntry = true;
        FastPageSwitch(this.context.pageContext, destination, recordHistoryEntry, {
            telemetryProperties: {
                ParentPageName: this.props.telemetryProps?.parentPageName,
                Scenario: "ViewFullReportClick",
                ReportyType: report
            }
        });
    };

    private async intitializeBoardsReportSelectorState() {
        try {
            await new PingServiceQuery("BoardsReportSelector", this.props.projectId).runQuery(this.context.pageContext);
            this.setState({ isReady: true }, this.props.onLoadCompleted);
        } catch (error) {
            // Other errors can be handled by the underlying component for now.
            const is403 = (error.message as string).indexOf("403") !== -1;
            if (is403) {
                publishEmptyReportTelemetry(this.context.pageContext, BoardsReportSelector.ReportTelemetryFeatureName, error.message);
            } else {
                traceException(this.context.pageContext, telemetryAreaName, BoardsReportSelector.ReportTelemetryFeatureName, error);
            }
            this.setState({ isReady: true, showAccessDeniedState: is403 }, this.props.onLoadCompleted);
        } finally {
            if (this.props.onLoadCompleted) {
                this.props.onLoadCompleted();
            }
        }
    }
}
VssComponent.register("BoardsReportSelector", BoardsReportSelector);
VssComponent.register("BoardsReportSelectorPreloader", NoOpComponent);
